import React, { useEffect, useState, useRef } from "react";

const SeamlessCarousel = ({ children, interval = 3000 }) => {
  const slides = React.Children.toArray(children);
  const length = slides.length;
  
  // Create a triple-buffer for seamless looping
  const displaySlides = [...slides, ...slides, ...slides];
  
  const [currentIndex, setCurrentIndex] = useState(length);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const timeoutRef = useRef(null);

  // Auto-play Logic
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (!isPaused) {
      timeoutRef.current = setTimeout(() => {
        handleNext();
      }, interval);
    }

    return () => clearTimeout(timeoutRef.current);
  }, [currentIndex, interval, isPaused]);

  const handleNext = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const handlePrev = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  const handleTransitionEnd = () => {
    if (currentIndex >= length * 2) {
      setIsTransitioning(false);
      setCurrentIndex(length);
    } else if (currentIndex <= length - 1) {
      setIsTransitioning(false);
      setCurrentIndex(length * 2 - 1);
    }
  };

  // Ensure transitions are re-enabled after a "jump"
  useEffect(() => {
    if (!isTransitioning) {
      const raf = requestAnimationFrame(() => {
        setIsTransitioning(true);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [isTransitioning]);

  return (
    <div 
      className="relative group w-full mx-auto overflow-hidden" 
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Navigation Buttons - Exactly like HorizontalCarousel */}
      <button
        type="button"
        onMouseDown={() => setIsPaused(true)}
        onClick={(e) => { e.stopPropagation(); handlePrev(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white bg-blue-600 w-12 h-12 flex items-center justify-center rounded-full shadow-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-blue-800 active:scale-95"
      >
        <span className="text-2xl font-bold">←</span>
      </button>

      <button
        type="button"
        onMouseDown={() => setIsPaused(true)}
        onClick={(e) => { e.stopPropagation(); handleNext(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white bg-blue-600 w-12 h-12 flex items-center justify-center rounded-full shadow-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-blue-800 active:scale-95"
      >
        <span className="text-2xl font-bold">→</span>
      </button>

      {/* Slider Container */}
      <div className="overflow-hidden">
        <div
          className="flex"
          onTransitionEnd={handleTransitionEnd}
          style={{
            // Note: 100/3 assumes 3 items visible. 
            // This needs to match the responsive w-full/sm:w-1/2/lg:w-1/3 logic below.
            transform: `translateX(-${currentIndex * (100 / 3)}%)`,
            transition: isTransitioning ? "transform 0.5s ease-in-out" : "none",
          }}
        >
          {displaySlides.map((slide, i) => (
            <div key={i} className="w-full sm:w-1/2 lg:w-1/3 flex-shrink-0 px-3 py-4">
              <div className="h-full w-full">{slide}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SeamlessCarousel;